/** DataBest — Trend line chart (multi-series). */
import React, { useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer,
} from "recharts";
import { formatDateUS } from "@/lib/dates";
import RefreshingOverlay from "./RefreshingOverlay";

const SERIES = [
    { key: "disposition", label: "Disposition", color: "#1e40af" },
    { key: "quality",     label: "Quality",     color: "#059669" },
    { key: "dependence",  label: "Dep",         color: "#7c3aed" },
    { key: "composite",   label: "Composite",   color: "#0f172a" },
];

const DashboardTrend = ({ trend, loading = false }) => {
    const [enabled, setEnabled] = useState({
        disposition: true, quality: true, dependence: true, composite: false,
    });
    const series = trend?.series || [];

    if (series.length === 0 && !loading) {
        return (
            <div
                className="flex h-[300px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 text-center"
                data-testid="trend-empty"
            >
                <p className="font-semibold text-slate-700">No trend yet</p>
                <p className="mt-1 text-xs text-slate-500">
                    Data will appear once scoring begins
                </p>
            </div>
        );
    }

    return (
        <div
            className="relative rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
            data-testid="dashboard-trend"
        >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-blue-950">
                    Trend over time
                </h3>
                <div className="flex flex-wrap gap-1.5">
                    {SERIES.map((s) => (
                        <button
                            key={s.key}
                            onClick={() =>
                                setEnabled((e) => ({ ...e, [s.key]: !e[s.key] }))
                            }
                            data-testid={`trend-toggle-${s.key}`}
                            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                                enabled[s.key]
                                    ? "border-transparent text-white"
                                    : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                            }`}
                            style={enabled[s.key] ? { backgroundColor: s.color } : {}}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className={`h-[280px] transition-opacity sm:h-[320px] ${loading ? "opacity-40" : ""}`}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            tickFormatter={(v) => formatDateUS(v)}
                            angle={-25}
                            textAnchor="end"
                            height={50}
                            interval="preserveStartEnd"
                        />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 12, border: "1px solid #e2e8f0",
                                fontSize: 12,
                            }}
                            labelFormatter={(v) => formatDateUS(v)}
                            formatter={(v) => (v == null ? "—" : v.toFixed(2))}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                        {SERIES.filter((s) => enabled[s.key]).map((s) => (
                            <Line
                                key={s.key}
                                type="monotone"
                                dataKey={s.key}
                                name={s.label}
                                stroke={s.color}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <RefreshingOverlay visible={loading} />
        </div>
    );
};

export default DashboardTrend;
